import { AuthContext } from "context/appContext";
import { ProfilClient } from "data/types";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, useContext, useEffect } from "react";
import { Helmet } from "react-helmet";
import { getUserStats } from "redux/features/Admin/Stripe/stripeSlice";
import CommonLayout from "./AccountPage/CommonLayout";

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { EASING } from "../../utils/easingUtils";
import { ChevronRight, Plus, TrendingUp, Calendar, MapPin, MessageSquare, Users } from "lucide-react";

export interface ClientDashboardProps {
  className?: string;
}

const ClientDashboard: FC<ClientDashboardProps> = ({ className = "" }) => {
  const authContext = useContext(AuthContext);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const dispatch = useAppDispatch();
  let profile = authContext.user?.profil as ProfilClient;
  const is_customer = authContext?.user?.isCustomer;

  useEffect(() => {
    dispatch(
      getUserStats({
        userId: profile?.id,
        account_id: profile?.account_id || "",
        status: profile?.validation || "",
      })
    );
  }, []);
  const stats = useAppSelector((state) => state?.stripe?.ownerStats);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: EASING.easeOut,
      },
    },
  };
  const subscribedOwnerStats = [
    {
      title: "Mes espaces",
      value: stats?.locationsCount,
      description: "Espaces que vous proposez",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/locations",
      trending: "+12%",
    },
    {
      title: "Réclamations",
      value: stats?.reservationsFeedbackCount,
      description: "Messages et retours",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/reclamations-reservations",
      trending: (stats?.reservationsFeedbackCount ?? 0) > 0 ? "Nouveaux" : "",
    },
    {
      title: "Espaces favoris",
      value: stats?.favoriLocationsCount,
      description: "Lieux sauvegardés",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/espaces-favoris",
      trending: (stats?.favoriLocationsCount ?? 0) > 0 ? "❤️" : "",
    },
    {
      title: "Prestations favoris",
      value: stats?.favoriServicesCount,
      description: "Services préférés",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/prestataires-favoris",
      trending: (stats?.favoriServicesCount ?? 0) > 0 ? "⭐" : "",
    },
    {
      title: "Mes événements",
      value: stats?.reservationsCount,
      description: "Événements organisés",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/evenements",
      trending: "+8%",
    },
  ];
  const ownerStats = [
    {
      title: "Demandes de réservation",
      value: stats?.pendingReservationsCount,
      description: "En attente d'approbation",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/list-pending-reservations",
      trending: (stats?.pendingReservationsCount ?? 0) > 0 ? "Urgent" : "",
    },
    {
      title: "Mes réservations",
      value: stats?.reservationsCount,
      description: "Réservations confirmées",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/mes-reservations",
      trending: "+8%",
    },
    {
      title: "Transferts",
      value: stats?.transfertsCount,
      description: "Paiements reçus",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/transferts-reservations",
      trending: "+15%",
    },
    {
      title: "Transactions",
      value: stats?.transactionsCount,
      description: "Historique financier",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/transactions",
      trending: "+5%",
    },
    {
      title: "Mes espaces",
      value: stats?.locationsCount,
      description: "Lieux proposés",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/locations",
      trending: "+12%",
    },
    {
      title: "Réclamations",
      value: stats?.reservationsFeedbackCount,
      description: "Support client",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/reclamations-reservations",
      trending: (stats?.reservationsFeedbackCount ?? 0) > 0 ? "Nouveaux" : "",
    },
    {
      title: "Espaces favoris",
      value: stats?.favoriLocationsCount,
      description: "Lieux préférés",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/espaces-favoris",
      trending: (stats?.favoriLocationsCount ?? 0) > 0 ? "❤️" : "",
    },
    {
      title: "Prestations favoris",
      value: stats?.favoriServicesCount,
      description: "Services favoris",
      gradient: "from-indigo-500 to-purple-500",
      bgGradient: "from-indigo-50 to-purple-50",
      href: "/client/prestataires-favoris",
      trending: (stats?.favoriServicesCount ?? 0) > 0 ? "⭐" : "",
    },
  ];

  const statisticCards = [
    {
      title: "Mes événements",
      value: stats?.reservationsCount,
      description: "Événements organisés",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/evenements",
      trending: "+8%",
    },
    {
      title: "Espaces favoris",
      value: stats?.favoriLocationsCount,
      description: "Lieux sauvegardés",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/espaces-favoris",
      trending: (stats?.favoriLocationsCount ?? 0) > 0 ? "❤️" : "",
    },
    {
      title: "Prestations favoris",
      value: stats?.favoriServicesCount,
      description: "Services préférés",
      gradient: "from-blue-600 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      href: "/client/prestataires-favoris",
      trending: (stats?.favoriServicesCount ?? 0) > 0 ? "⭐" : "",
    },
  ];

  const combinedStats =
    profile?.validation === "approuvé" && is_customer !== true
      ? ownerStats
      : profile?.validation === "approuvé" && is_customer === true
        ? subscribedOwnerStats
        : statisticCards;

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <Helmet>
        <title>Dashboard - EventToBee | Gérez vos espaces et événements</title>
        <meta
          name="description"
          content="Tableau de bord EventToBee - Gérez vos espaces, réservations et événements en toute simplicité"
        />
      </Helmet>

      <CommonLayout>
        <div className="relative overflow-hidden bg-gradient-to-br from-purple-50/30 via-blue-50/20 to-cyan-50/30 dark:from-purple-900/20 dark:via-blue-900/10 dark:to-cyan-900/20 min-h-screen">
          {/* Background Effects */}
          <div className="absolute inset-0">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-purple-300/10 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  opacity: [0.2, 0.5, 0.2],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  delay: Math.random() * 8,
                }}
              />
            ))}
          </div>

          <div className="relative z-10 px-4 py-8" ref={ref}>
            {/* Header Section */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                    Tableau de bord
                  </h1>
                  <p className="text-lg text-gray-600 dark:text-gray-400">
                    Bonjour {profile?.name || "Client"} ! Gérez vos activités sur EventToBee
                  </p>
                </div>

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center space-x-2"
                >
                  <Link
                    to="/add-location"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Ajouter un espace
                  </Link>
                </motion.div>
              </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12"
            >
              {combinedStats.map((card, index) => (
                <motion.div
                  key={card.title}
                  variants={cardVariants}
                  whileHover={{
                    y: -5,
                    scale: 1.02,
                    transition: { duration: 0.2 },
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link to={card.href}>
                    <div
                      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${card.bgGradient || "from-white to-gray-50"} dark:from-gray-800 dark:to-gray-900 p-6 shadow-lg hover:shadow-xl border border-white/20 dark:border-gray-700/30 backdrop-blur-sm transition-all duration-300 h-40 flex flex-col justify-between`}
                    >
                      {/* Gradient Background */}
                      <div
                        className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${card.gradient} opacity-10 rounded-full blur-xl`}
                      />

                      {/* Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1" />
                        {card.trending && (
                          <div
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              card.trending.includes("+")
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                : card.trending === "Urgent"
                                  ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                  : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                            }`}
                          >
                            {card.trending}
                          </div>
                        )}
                      </div>

                      {/* Value and Title */}
                      <div className="flex-1 flex flex-col justify-center">
                        <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                          {card.value ?? 0}
                        </div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 text-center leading-tight break-words">
                          {card.title}
                        </h3>
                      </div>

                      {/* Description */}
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-600 dark:text-gray-400 flex-1">{card.description}</p>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-purple-500 transition-colors ml-2" />
                      </div>

                      {/* Hover Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>

            {/* Quick Actions Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-3xl p-8 border border-white/20 dark:border-gray-700/30 shadow-xl"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <TrendingUp className="w-6 h-6 text-purple-600 mr-3" />
                Actions rapides
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {(() => {
                  // Actions rapides adaptées selon le profil utilisateur
                  const baseActions = [
                    {
                      title: "Parcourir les espaces",
                      icon: MapPin,
                      href: "/espace-evenementiel",
                      color: "from-blue-500 to-cyan-500",
                    },
                    {
                      title: "Messages",
                      icon: MessageSquare,
                      href: "/client/messages",
                      color: "from-purple-500 to-violet-500",
                    },
                  ];

                  if (profile?.validation === "approuvé" && is_customer !== true) {
                    // Actions pour hôtes propriétaires d'espaces
                    return [
                      {
                        title: "Demandes en attente",
                        icon: Calendar,
                        href: "/client/list-pending-reservations",
                        color: "from-red-500 to-orange-500",
                        badge:
                          stats?.pendingReservationsCount && stats.pendingReservationsCount > 0
                            ? stats.pendingReservationsCount
                            : null,
                      },
                      {
                        title: "Ajouter un espace",
                        icon: Plus,
                        href: "/add-location",
                        color: "from-green-500 to-emerald-500",
                      },
                      {
                        title: "Mes espaces",
                        icon: MapPin,
                        href: "/client/locations",
                        color: "from-blue-500 to-cyan-500",
                      },
                      {
                        title: "Mes réservations",
                        icon: Users,
                        href: "/client/mes-reservations",
                        color: "from-purple-500 to-violet-500",
                      },
                    ];
                  } else if (profile?.validation === "approuvé" && is_customer === true) {
                    // Actions pour hôtes avec abonnement
                    return [
                      {
                        title: "Demandes en attente",
                        icon: Calendar,
                        href: "/client/list-pending-reservations",
                        color: "from-red-500 to-orange-500",
                        badge:
                          stats?.pendingReservationsCount && stats.pendingReservationsCount > 0
                            ? stats.pendingReservationsCount
                            : null,
                      },
                      {
                        title: "Ajouter un espace",
                        icon: Plus,
                        href: "/add-location",
                        color: "from-green-500 to-emerald-500",
                      },
                      {
                        title: "Mes espaces",
                        icon: MapPin,
                        href: "/client/locations",
                        color: "from-blue-500 to-cyan-500",
                      },
                      {
                        title: "Mon abonnement",
                        icon: TrendingUp,
                        href: "/client/abonnement",
                        color: "from-purple-500 to-violet-500",
                      },
                    ];
                  } else {
                    // Actions pour clients événementiels
                    return [
                      {
                        title: "Créer un événement",
                        icon: Calendar,
                        href: "/client/evenements",
                        color: "from-green-500 to-emerald-500",
                      },
                      {
                        title: "Mes événements",
                        icon: Users,
                        href: "/client/mes-evenements",
                        color: "from-purple-500 to-violet-500",
                      },
                      ...baseActions,
                    ];
                  }
                })().map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <motion.div
                      key={action.title}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={inView ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                    >
                      <Link
                        to={action.href}
                        className="block p-4 rounded-xl bg-white dark:bg-gray-800 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 relative"
                      >
                        <div className="flex items-center space-x-3 min-w-0">
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${action.color} relative`}>
                            <Icon className="w-5 h-5 text-white" />
                            {action.badge && (
                              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                                {action.badge}
                              </span>
                            )}
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white text-sm truncate flex-1">
                            {action.title}
                          </span>
                          {action.badge && (
                            <span className="ml-auto bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300 text-xs font-semibold px-2 py-1 rounded-full">
                              {action.badge} nouveau{action.badge > 1 ? "x" : ""}
                            </span>
                          )}
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </CommonLayout>
    </div>
  );
};

export default ClientDashboard;
